<template>
  <div class="status-ring">
    <!--
      The ring is the pin's own status arc, drawn INSIDE its circle: a faint
      track, then the accent arc for the reading (0–100). It paints above the
      slot (`z-index: 2`) so a full-bleed thumbnail cannot hide it, and it is
      inert — taps and holds belong to whatever the pin already wired.
      `rotate(-90deg)` puts the dash's start at 12 o'clock instead of 3.
    -->
    <svg class="status-ring__svg" viewBox="0 0 100 100" aria-hidden="true" focusable="false">      
      <circle
        v-if="arcLength > 0"
        class="status-ring__arc"
        cx="50"
        cy="50"
        :r="RING_RADIUS"
        :stroke-dasharray="`${arcLength} ${CIRCUMFERENCE}`"
        :style="{ stroke: accentColor }" />
    </svg>
    <div class="status-ring__content"><slot /></div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    /**
     * The reading the arc shows, 0–100. `null` (an unknown reading) draws the
     * track only — the same picture a `0` draws, because a zero-length dash
     * with round caps would still paint a dot.
     */
    value?: number | null;
    /**
     * The arc's colour. The default is the pin's own token, so a widget that
     * already sets `--pin-active-color` says the same thing here; a pin with
     * neither falls back to the neutral the sheet uses for untyped readings.
     */
    accent?: string;
  }>(),
  { value: null },
);

/** The ring dial's geometry (`ControlRingDial`): r=44 on a 100-box, 9 % stroke. */
const RING_RADIUS = 44;
const CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const accentColor = computed(() => props.accent ?? 'var(--pin-active-color, rgb(148, 163, 184))');

const arcLength = computed(() => {
  if (props.value === null || props.value === undefined) return 0;
  const clamped = Math.max(0, Math.min(100, props.value));
  return (clamped / 100) * CIRCUMFERENCE;
});
</script>

<style scoped>
.status-ring {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-ring__svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
  transform: rotate(-90deg); /* the dash starts at 12 o'clock, not 3 */
  z-index: 2;
}

/* The pin is a dark badge in BOTH themes (`--device-icon-pin` background), so
   the track is one colour in both: a hairline that reads on that fill. */
.status-ring__track {
  fill: none;
  stroke: rgba(148, 163, 184, 0.35);
  stroke-width: 9;
}

.status-ring__arc {
  fill: none;
  stroke-width: 9;
  stroke-linecap: round;
  transition: stroke-dasharray 0.3s ease-out;
}

/* The widget's own content sits centered in the ring's clear circle. */
.status-ring__content {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  /* The pin is a dark badge in BOTH themes, so its glyph must not inherit the
     page's text colour (which is near-black in the light theme). This is the
     grey the pins have always drawn their Material glyphs in; a widget that
     wants its accent on the glyph sets `color` on it inline. */
  color: rgba(200, 200, 200, 0.9);
}
</style>
